"use client";

import React, { useState, useEffect } from "react";
import { Sparkles, ArrowRight, CircleCheck as CheckCircle2, ChevronRight, Activity } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const benefitCards = [{
  num: "01",
  title: "AI Search Dominance",
  description: "When potential clients ask ChatGPT, Claude, or Perplexity for local services, your business comes up first.",
  tag: "Visibility"
}, {
  num: "02",
  title: "Clean Web Architecture",
  description: "High-speed, structured landing pages built to instantly convert AI-driven visitors into booked calendar appointments.",
  tag: "Conversion"
}, {
  num: "03",
  title: "Automated Review Loops",
  description: "A systematic engine that continuously harvests 5-star feedback to build unstoppable search engine authority.",
  tag: "Reputation"
}, {
  num: "04",
  title: "Monthly Visibility Tracking",
  description: "Clear, quantifiable reporting tracking your brand's exact prompt share and ranking across all major AI engines.",
  tag: "Analytics"
}];
const ecosystemNodes = [{
  id: "node-1",
  label: "LLM & AI Citation Engine",
  subtitle: "Knowledge Graph & Structured Schema",
  badge: "Layer 01",
  summary: "Structures your business data, JSON-LD schema, and local citations so generative engines parse your services accurately without hallucination.",
  stat: "100% Indexing Precision",
  actionText: "Learn About Citations",
  actionLink: "/AICitationsPage"
}, {
  id: "node-2",
  label: "High-Speed Landing Experience",
  subtitle: "Conversion-Focused Tech Stack",
  badge: "Layer 02",
  summary: "Ultra-fast, mobile-optimized pages designed to grab interest immediately when users arrive from generative search queries.",
  stat: "<0.8s Target Load Time",
  actionText: "Explore Web Design",
  actionLink: "/WebsiteDesignPage"
}, {
  id: "node-3",
  label: "Automated Reputation Engine",
  subtitle: "Proactive Feedback Loops",
  badge: "Layer 03",
  summary: "Workflows that request and aggregate positive customer reviews, feeding continuous trust signals directly into AI indexers.",
  stat: "+340% Review Growth",
  actionText: "See Review Systems",
  actionLink: "/ReputationManagementPage"
}, {
  id: "node-4",
  label: "Real-Time AI Analytics Hub",
  subtitle: "Multi-Model Prompt Audit",
  badge: "Layer 04",
  summary: "Comprehensive reporting tracking prompt placement, competitor benchmarks, and converted leads from conversational search.",
  stat: "24/7 Position Auditing",
  actionText: "View Analytics Demo",
  actionLink: "/AnalyticsReportingPage"
}];
export default function Ecosystem() {
  const [activeNodeId, setActiveNodeId] = useState<string>("node-1");
  const activeNode = ecosystemNodes.find(node => node.id === activeNodeId) || ecosystemNodes[0];
  useEffect(() => {
    if (typeof window === "undefined") return;
    gsap.registerPlugin(ScrollTrigger);
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mediaQuery.matches) return;
    const ctx = gsap.context(() => {
      gsap.from(".gsap-benefit-card", {
        opacity: 0,
        y: 24,
        duration: 0.5,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".gsap-benefit-grid",
          start: "top 85%",
          once: true
        }
      });
    });
    return () => ctx.revert();
  }, []);
  return <section id="whyitmattersecosystem" data-nav="light" className="relative w-full bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {}
        <div className="max-w-3xl mb-16">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-muted text-muted-foreground font-mono text-xs uppercase tracking-wider mb-6 border border-border">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive"></span>
            </span>
            <span>AI-First Positioning Architecture</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default mb-6 leading-tight">
            Why generative visibility matters for your{" "}
            <span className="font-serif italic !text-[var(--accent-deep-red)]">local growth</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            When potential customers ask AI assistants who to hire, traditional SEO signals are no longer enough. ClearLine connects your business directly to the intelligence layer where choices are recommended.
          </p>
        </div>

        {}
        <div className="gsap-benefit-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {benefitCards.map((card, i) => <div key={card.num} data-index={i} className="gsap-benefit-card h-full">
              <Card className="h-full bg-card text-card-foreground border border-border hover:border-primary/40 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-xs font-semibold text-muted-foreground group-hover:text-primary transition-colors">
                      [{card.num}]
                    </span>
                    <span className="text-[10px] font-mono tracking-wider uppercase px-2 py-0.5 rounded bg-muted text-muted-foreground">
                      {card.tag}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground group-hover:text-primary transition-colors">
                    {card.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                    {card.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {}
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
              Integrated Technology
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-default">
              The ClearLine AI Ecosystem
            </h3>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            Hover or click an architectural layer to examine how data flows from your online presence straight into top generative models.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch mb-20">
          {}
          <div className="lg:col-span-5 flex flex-col bg-card border border-border rounded-xl overflow-hidden shadow-sm">
            {ecosystemNodes.map((node, i) => {
            const isActive = activeNodeId === node.id;
            return <div key={node.id} data-index={i} className="flex flex-col">
                  <Button variant="ghost" onClick={() => setActiveNodeId(node.id)} onMouseEnter={() => setActiveNodeId(node.id)} className={`w-full justify-start text-left h-auto py-5 px-6 rounded-none transition-all border-l-2 ${isActive ? "bg-accent/80 text-accent-foreground border-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40"}`}>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-muted-foreground">
                            {node.badge}
                          </span>
                          <span className="font-semibold text-base text-foreground">
                            {node.label}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground font-normal">
                          {node.subtitle}
                        </span>
                      </div>
                      <ChevronRight className={`size-4 transition-transform ${isActive ? "rotate-90 text-primary" : "opacity-40"}`} />
                    </div>
                  </Button>
                  {i < ecosystemNodes.length - 1 && <Separator className="bg-border" />}
                </div>;
          })}
          </div>

          {}
          <div className="lg:col-span-7 bg-muted/30 border border-border rounded-xl p-6 sm:p-8 flex flex-col justify-between shadow-sm relative min-h-[300px]">
            <AnimatePresence mode="wait">
              <motion.div key={activeNode.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="flex flex-col justify-between h-full">
                <div>
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <span className="font-mono text-xs px-3 py-1 rounded text-primary-foreground font-medium !bg-[var(--accent-deep-red)]">
                      {activeNode.badge}
                    </span>
                    <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                      <Activity className="size-3.5 text-primary" />
                      <span>Live Core Module</span>
                    </div>
                  </div>

                  <h4 className="text-2xl font-bold text-foreground mb-3 font-default">
                    {activeNode.label}
                  </h4>
                  <p className="text-base text-muted-foreground leading-relaxed mb-6 font-sans">
                    {activeNode.summary}
                  </p>
                </div>

                <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="font-mono text-sm font-semibold text-foreground">
                      {activeNode.stat}
                    </span>
                  </div>

                  <Button asChild variant="default" size="sm" className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive text-primary-foreground shadow-xs h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 group !bg-[var(--accent-deep-red)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">
                    <Link to={activeNode.actionLink}>
                      <span>{activeNode.actionText}</span>
                      <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {}
        <div className="relative rounded-2xl bg-card border border-border p-8 sm:p-12 lg:p-16 text-center overflow-hidden shadow-lg">
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-muted-foreground font-mono text-xs">
              <Sparkles className="size-3.5 text-primary" />
              <span>Ready for generative search?</span>
            </div>

            <h3 className="text-3xl sm:text-4xl font-bold text-card-foreground font-default tracking-tight">
              Ensure your business gets recommended when prospects ask AI.
            </h3>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Claim your complimentary AI visibility checkup or schedule a direct strategy session with our technical team today.
            </p>

            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive text-primary-foreground shadow-xs h-10 rounded-md px-6 has-[>svg]:px-4 w-full font-medium group !bg-[var(--accent-deep-red)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 sm:w-auto">
                <Link className="underline cursor-pointer" to="/PricingPlansPage">
                  <span>get started now</span>
                  <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/VisibilityCheckupPage">
                  <span>Get Free AI Checkup</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}